import React, { useState } from 'react';
import Title from '../components/common/Title';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const FAQPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  const categories = [
    { id: 'all', name: 'All FAQs' },
    { id: 'account', name: 'Account & Profile' },
    { id: 'subscription', name: 'Subscription & Billing' },
    { id: 'content', name: 'Content & Features' },
    { id: 'technical', name: 'Technical Issues' },
  ];
  
  const faqs: FAQItem[] = [
    {
      id: 1,
      question: 'How do I create an account?',
      answer: 'To create an account, click the "Sign In" button at the top right corner of our website, then click "Sign up now". Follow the on-screen instructions to complete your registration with your email address and create a password.',
      category: 'account'
    },
    {
      id: 2,
      question: 'How do I reset my password?',
      answer: 'If you\'ve forgotten your password, go to the Sign In page and click "Forgot Password". Enter the email address associated with your account, and we\'ll send you instructions to reset your password.',
      category: 'account'
    },
    {
      id: 3,
      question: 'How do I update my profile information?',
      answer: 'You can update your profile information by going to the Profile page, then clicking "Edit Profile". From there, you can modify your name, email address, password, and other account settings.',
      category: 'account'
    },
    {
      id: 4,
      question: 'What subscription plans do you offer?',
      answer: 'We offer three subscription plans: Basic (SD quality, 1 screen), Standard (HD quality, 2 screens), and Premium (Ultra HD/4K, 4 screens). Visit our Pricing page for current rates and features.',
      category: 'subscription'
    },
    {
      id: 5,
      question: 'How do I change my subscription plan?',
      answer: 'To change your subscription plan, go to your Account page, select "Subscription", and click "Change Plan". From there, you can upgrade or downgrade your subscription.',
      category: 'subscription'
    },
    {
      id: 6,
      question: 'How do I cancel my subscription?',
      answer: 'You can cancel your subscription at any time by going to your Account page, selecting "Subscription", and clicking "Cancel Subscription". Your access will continue until the end of your current billing period.',
      category: 'subscription'
    },
    {
      id: 7,
      question: 'When will I be charged?',
      answer: 'We charge your payment method on the same day each month, based on your signup date. For example, if you signed up on the 15th, you\'ll be charged on the 15th of each month.',
      category: 'subscription'
    },
    {
      id: 8,
      question: 'How do I add a movie or TV show to My List?',
      answer: 'To add a title to My List, hover over any title and click the + icon. You can also add it from the title\'s detail page. To view your list, click on "My List" in the navigation menu.',
      category: 'content'
    },
    {
      id: 9,
      question: 'Why isn\'t a specific movie or show available?',
      answer: 'Content availability varies by region due to licensing agreements. If you can\'t find a specific title, it may not be available in your region or it may have been rotated out of our library. You can request content using the "Request a Title" feature.',
      category: 'content'
    },
    {
      id: 10,
      question: 'When will new episodes of a series be added?',
      answer: 'For ongoing series, new episodes are typically added based on their original release schedule. For some shows, we add new episodes weekly, while for others, we may add the entire season at once.',
      category: 'content'
    },
    {
      id: 11,
      question: 'What devices can I use to watch Quick Flick?',
      answer: 'You can watch Quick Flick on most internet-connected devices, including smart TVs, game consoles, streaming media players, mobile phones, tablets, and computers. Visit our Supported Devices page for a complete list.',
      category: 'technical'
    },
    {
      id: 12,
      question: 'What internet speed do I need for streaming?',
      answer: 'For SD quality, we recommend at least 3 Mbps. For HD quality, 5 Mbps is recommended. For Ultra HD/4K streaming, we recommend at least 25 Mbps. You can check your internet speed in the account settings.',
      category: 'technical'
    },
    {
      id: 13,
      question: 'Why is my video buffering or loading slowly?',
      answer: 'Buffering issues are typically caused by slow internet connections. Try closing other applications that use bandwidth, connecting to a closer Wi-Fi router, or using an Ethernet cable for a direct connection. You can also lower the video quality in your playback settings.',
      category: 'technical'
    }
  ];
  
  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);
  
  return (
    <>
      <Title title="FAQ" />
      <main>
        <div className="container">
          <div style={{ marginTop: '120px', marginBottom: '50px' }}>
            <h1 className="section-title">Frequently Asked Questions</h1>
            
            <div style={{ marginTop: '30px' }}>
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '30px' }}>
                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                      style={{ 
                        backgroundColor: activeCategory === category.id ? 'var(--primary-color)' : '',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    style={{ 
                      width: '100%',
                      padding: '12px 15px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      color: 'var(--text-color)',
                      marginBottom: '20px'
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {filteredFAQs.map(faq => (
                    <div
                      key={faq.id}
                      style={{ 
                        background: 'var(--secondary-color)',
                        borderRadius: '8px',
                        padding: '20px'
                      }}
                    >
                      <h3 style={{ marginBottom: '10px' }}>{faq.question}</h3>
                      <p style={{ color: 'var(--light-gray)', lineHeight: '1.6' }}>{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{ 
                background: 'var(--secondary-color)',
                borderRadius: '8px',
                padding: '30px',
                textAlign: 'center',
                marginTop: '40px'
              }}>
                <h2 style={{ marginBottom: '15px' }}>Still Need Help?</h2>
                <p style={{ marginBottom: '20px' }}>
                  Can't find the answer you're looking for? Our customer support team is here to help.
                </p>
                <a href="/contact" className="btn btn-primary">Contact Us</a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default FAQPage; 